// src/intake/IntakeApp.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Brief 23 — Main intake container: state, navigation, welcome/completion
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { questions } from './questions';
import IntakeQuestion from './IntakeQuestion';
import IntakeProgress from './IntakeProgress';
import { submitIntake } from './submitIntake';
import './intake.css';

type Screen = 'welcome' | 'questions' | 'complete';

export default function IntakeApp() {
  const [screen, setScreen] = useState<Screen>('welcome');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fadeKey, setFadeKey] = useState(0);

  // Filter questions based on conditional showIf
  const activeQuestions = useMemo(() => {
    return questions.filter((q) => !q.showIf || q.showIf(answers));
  }, [answers]);

  const safeIndex = Math.min(currentIndex, Math.max(0, activeQuestions.length - 1));
  const currentQuestion = activeQuestions[safeIndex];
  const isLastQuestion = safeIndex === activeQuestions.length - 1;
  const currentValue = currentQuestion ? answers[currentQuestion.id] || '' : '';

  const canAdvance = useCallback(() => {
    if (!currentQuestion) return false;
    if (currentQuestion.type === 'loom') return true;
    if (!currentQuestion.required) return true;
    return currentValue.trim() !== '';
  }, [currentQuestion, currentValue]);

  const handleChange = useCallback((value: string) => {
    if (!currentQuestion) return;
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: value }));
  }, [currentQuestion]);

  const goNext = useCallback(async () => {
    if (!canAdvance()) return;

    if (isLastQuestion) {
      // Submit
      setSubmitting(true);
      setError(null);
      try {
        await submitIntake(answers);
        setSubmitted(true);
        setScreen('complete');
      } catch (err: any) {
        setError(err.message || 'Something went wrong. Please try again.');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setFadeKey((k) => k + 1);
    setCurrentIndex(safeIndex + 1);
  }, [canAdvance, isLastQuestion, answers, safeIndex]);

  const goBack = useCallback(() => {
    if (safeIndex > 0) {
      setFadeKey((k) => k + 1);
      setCurrentIndex(safeIndex - 1);
    }
  }, [safeIndex]);

  // Keyboard: Enter advances (handled in IntakeQuestion for non-textarea)
  // Also handle global Enter when focused on buttons
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (screen !== 'questions') return;
      if (e.key === 'Enter' && e.target instanceof HTMLButtonElement) {
        goNext();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [screen, goNext]);

  const containerStyle: React.CSSProperties = {
    background: '#001F8C',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Montserrat', sans-serif",
  };

  // ── WELCOME SCREEN ──────────────────────────────────────────────────

  if (screen === 'welcome') {
    return (
      <div style={containerStyle}>
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '32px 24px',
          textAlign: 'center',
        }}>
          {/* Logo mark */}
          <div style={{
            width: 80,
            height: 80,
            borderRadius: 16,
            background: 'rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 32,
            fontSize: 40,
            fontWeight: 700,
            color: '#64DAD2',
            fontFamily: "'Montserrat', sans-serif",
          }}>
            M
          </div>

          <h1 style={{
            color: '#fff',
            fontSize: 32,
            fontWeight: 700,
            marginBottom: 24,
            fontFamily: "'Montserrat', sans-serif",
            lineHeight: 1.2,
          }}>
            Your Financial Freedom Session
          </h1>

          <div style={{
            color: 'rgba(255,255,255,0.7)',
            fontSize: 18,
            lineHeight: 1.7,
            maxWidth: 440,
            marginBottom: 40,
            fontFamily: "'Montserrat', sans-serif",
          }}>
            <p style={{ marginBottom: 16 }}>
              Before we meet, I want to make sure<br />
              every minute of your session counts.
            </p>
            <p style={{ marginBottom: 0 }}>
              This takes about 8 minutes.<br />
              Estimates are completely fine —<br />
              we'll refine everything together.
            </p>
          </div>

          <button
            onClick={() => setScreen('questions')}
            className="intake-btn"
            style={{
              background: '#64DAD2',
              color: '#001F8C',
              border: 'none',
              borderRadius: 8,
              padding: '16px 40px',
              fontSize: 18,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: "'Montserrat', sans-serif",
              minHeight: 52,
              transition: 'opacity 150ms ease',
            }}
          >
            Let's begin →
          </button>
        </div>
      </div>
    );
  }

  // ── COMPLETION SCREEN ───────────────────────────────────────────────

  if (screen === 'complete') {
    const clientName = answers.clientName || '';
    return (
      <div style={containerStyle}>
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '32px 24px',
          textAlign: 'center',
        }}>
          {/* Checkmark */}
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(100,218,210,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 32,
            fontSize: 40,
            color: '#64DAD2',
          }}>
            ✓
          </div>

          <h1 style={{
            color: '#fff',
            fontSize: 32,
            fontWeight: 600,
            marginBottom: 20,
            fontFamily: "'Montserrat', sans-serif",
          }}>
            Thank you{clientName ? `, ${clientName.split(' ')[0]}` : ''}.
          </h1>

          <div style={{
            color: 'rgba(255,255,255,0.7)',
            fontSize: 18,
            lineHeight: 1.7,
            maxWidth: 440,
            marginBottom: 40,
            fontFamily: "'Montserrat', sans-serif",
          }}>
            <p style={{ marginBottom: 0 }}>
              I'll have everything prepared before we meet.<br />
              If anything changes in the meantime,<br />
              just reply to my email.
            </p>
          </div>

          <span style={{
            color: '#64DAD2',
            fontSize: 14,
            fontWeight: 500,
            fontFamily: "'Montserrat', sans-serif",
            letterSpacing: 1,
          }}>
            motion accountancy
          </span>
        </div>
      </div>
    );
  }

  // ── QUESTIONS SCREEN ────────────────────────────────────────────────

  return (
    <div style={containerStyle}>
      {/* Top bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 24px',
        flexShrink: 0,
      }}>
        <span style={{
          color: '#fff',
          fontSize: 14,
          fontWeight: 500,
          fontFamily: "'Montserrat', sans-serif",
          opacity: 0.8,
        }}>
          Motion Accountancy
        </span>
        <IntakeProgress current={safeIndex + 1} total={activeQuestions.length} />
      </div>

      {/* Progress bar full width */}
      <div style={{
        width: '100%',
        height: 4,
        background: 'rgba(255,255,255,0.15)',
        flexShrink: 0,
      }}>
        <div style={{
          width: `${((safeIndex + 1) / activeQuestions.length) * 100}%`,
          height: '100%',
          background: '#64DAD2',
          transition: 'width 300ms ease',
        }} />
      </div>

      {/* Main content */}
      <div style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        overflow: 'auto',
      }}>
        <div
          key={fadeKey}
          className="intake-fade-active"
          style={{
            width: '100%',
            maxWidth: 640,
            padding: '32px 24px',
          }}
        >
          {currentQuestion && (
            <IntakeQuestion
              question={currentQuestion}
              value={currentValue}
              onChange={handleChange}
              onNext={goNext}
              questionNumber={safeIndex + 1}
              totalQuestions={activeQuestions.length}
            />
          )}

          {/* Error message */}
          {error && (
            <div style={{
              marginTop: 16,
              padding: '12px 16px',
              background: 'rgba(239,68,68,0.15)',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 8,
              color: '#fca5a5',
              fontSize: 14,
              fontFamily: "'Montserrat', sans-serif",
            }}>
              {error}
              <button
                onClick={goNext}
                style={{
                  marginLeft: 12,
                  background: 'none',
                  border: 'none',
                  color: '#64DAD2',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bottom navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 24px',
        maxWidth: 640,
        width: '100%',
        margin: '0 auto',
        flexShrink: 0,
      }}>
        <button
          onClick={goBack}
          disabled={safeIndex === 0}
          className="intake-btn"
          style={{
            background: 'none',
            border: 'none',
            color: safeIndex === 0 ? 'rgba(255,255,255,0.2)' : '#fff',
            cursor: safeIndex === 0 ? 'default' : 'pointer',
            fontSize: 15,
            fontWeight: 500,
            fontFamily: "'Montserrat', sans-serif",
            padding: '12px 16px',
            minHeight: 52,
          }}
        >
          ← Back
        </button>

        {/* Don't show Next for select (auto-advances) unless nothing selected */}
        {(currentQuestion?.type !== 'select' || !currentValue) && (
          <button
            onClick={goNext}
            disabled={!canAdvance() || submitting}
            className="intake-btn"
            style={{
              background: canAdvance() && !submitting ? '#64DAD2' : 'rgba(100,218,210,0.3)',
              color: '#001F8C',
              border: 'none',
              borderRadius: 8,
              padding: '12px 32px',
              fontSize: 16,
              fontWeight: 600,
              cursor: canAdvance() && !submitting ? 'pointer' : 'default',
              fontFamily: "'Montserrat', sans-serif",
              minHeight: 52,
              transition: 'all 150ms ease',
            }}
          >
            {submitting
              ? 'Submitting...'
              : isLastQuestion
                ? 'Submit'
                : currentQuestion?.type === 'loom'
                  ? 'Continue'
                  : 'Next →'
            }
          </button>
        )}
      </div>
    </div>
  );
}
